/**
 * ScheduledItem — source-neutral schedule item contract (plan 2026-06-16-006, U1).
 *
 * A single forward-looking item in a section's schedule, plus the response
 * envelope the section-scoped schedule read returns. The contract is
 * offering-type-NEUTRAL by design: athletics is the only projector wired today
 * (DB-backed `AthleticGame`, RSVP-capable), but the route, field names, and this
 * shape stay source-agnostic so a future academic class-schedule projector
 * becomes a second projector behind the SAME contract — not a parallel stack.
 * Mirrors the "unify at the contract, not the table" precedent of `SchoolEvent`.
 *
 * Self-only projection (security): `myResponse`/`myNote` are ALWAYS the calling
 * viewer's OWN response/note and never a peer's. Peer/aggregate availability
 * lives only behind the coach-gated summary endpoint. The split is enforced at
 * the service layer (a distinct self-only projector), not here.
 *
 * Untrusted text: `title`, `subtitle`, `opponent`, `location`, and `myNote` are
 * bounded PLAINTEXT — no HTML passthrough. RN `<Text>` renders them safely; any
 * web/HTML render path MUST escape them (the Events Explorer Mapbox `setHTML`
 * stored-XSS precedent). The read path treats stored notes as untrusted
 * regardless of any write-path constraint (pre-write-API rows may exist).
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-16-006-feat-section-schedule-read-surface-plan.md (U1)
 */

import { z } from 'zod'
import { ATHLETIC_AVAILABILITY_NOTE_MAX, AthleticAvailabilityStatusSchema } from './athletic-availability'

// ---------------------------------------------------------------------------
// Kind enum
// ---------------------------------------------------------------------------

/**
 * What kind of scheduled item this is.
 *
 * `GAME | PRACTICE` ONLY (KTD5). The academic `MEETING` kind is deliberately
 * NOT shipped here — origin R3 reserves it until a second (academic) projector
 * is actually planned, and an exported enum member is a wire contract costly to
 * remove once a mobile build ships it. Extend this enum when that projector lands.
 */
export const ScheduledItemKindSchema = z.enum(['GAME', 'PRACTICE'])
export type ScheduledItemKind = z.infer<typeof ScheduledItemKindSchema>

// ---------------------------------------------------------------------------
// Status (response envelope discriminator)
// ---------------------------------------------------------------------------

/**
 * Server-derived schedule status (KTD4). The client never infers these — the
 * service computes them from concrete signals:
 *  - `NOT_ENABLED` — athletics sync is not configured for the school
 *    (`platformDataSyncConfig` ATHLETIC_EVENTS gate), OR the caller is not a
 *    member of the section (uniform no-leak shape). NOT derived from zero games.
 *  - `INCOMPLETE` — enabled, but THIS section's resolution looks incomplete
 *    (≈zero resolved forward games AND the school has unlinked athletic games).
 *  - `ENABLED_EMPTY` — enabled, section resolved, genuinely zero forward games.
 *  - `OK` — forward items exist.
 */
export const ScheduleStatusSchema = z.enum(['OK', 'NOT_ENABLED', 'INCOMPLETE', 'ENABLED_EMPTY'])
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/**
 * Per-item capability flags. `canRSVP` is athletics-only for now. Room is left
 * for a future `canManage` (coach control) — this read does NOT populate it.
 */
export const ScheduledItemCapabilitiesSchema = z.object({
  /** Whether the viewer may submit/change an RSVP for this item (athletics only today). */
  canRSVP: z.boolean()
})
export type ScheduledItemCapabilities = z.infer<typeof ScheduledItemCapabilitiesSchema>

// ---------------------------------------------------------------------------
// ScheduledItem
// ---------------------------------------------------------------------------

/**
 * One forward-looking schedule item. Always sourced from a projector; never a
 * raw DB row. `id` is a public CUID, never an internal DB bigint and never a
 * Blackbaud id.
 */
export const ScheduledItemSchema = z.object({
  /** Public CUID of the underlying item (e.g. the athletic game). */
  id: z.string(),
  /** GAME or PRACTICE (KTD5). */
  kind: ScheduledItemKindSchema,
  /**
   * Start instant as a true-UTC ISO 8601 string. The athletics projector
   * re-anchors the BB-stored "local-wall-clock-mislabeled-as-UTC" `gameDate`
   * through the school timezone before emitting this (KTD3a). Use for sorting.
   */
  startsAt: z.string(),
  /** Optional end instant (ISO 8601). Athletics rarely supplies this. */
  endsAt: z.string().nullable().optional(),
  /**
   * True when the source carries a DATE but no time of day — Blackbaud's
   * `time_type: "TBD"`, ~15% of athletic items fleet-wide. `startsAt` is then
   * the school's midnight, which is a DATE ANCHOR and not a real start time.
   *
   * Clients MUST render "Time TBD" (or omit the time) rather than formatting
   * `startsAt` — a confident "12:00 AM" on a practice is the bug this field
   * exists to prevent. Optional with a `false` default so an older client that
   * predates the field keeps its current behavior instead of failing to parse.
   */
  timeTbd: z.boolean().optional().default(false),
  /** Human-readable title (untrusted plaintext — no HTML). */
  title: z.string().nullable().optional(),
  /** Optional secondary line (untrusted plaintext — no HTML). */
  subtitle: z.string().nullable().optional(),
  /** Opponent name for a GAME (untrusted plaintext — no HTML). */
  opponent: z.string().nullable().optional(),
  /** Home / Away / Neutral as supplied by the source. */
  homeAway: z.string().nullable().optional(),
  /** Venue / location name (untrusted plaintext — no HTML). */
  location: z.string().nullable().optional(),
  /**
   * The CALLER'S OWN RSVP only (self-only, KTD5/R2). `IN | OUT | MAYBE`, or
   * null for no-response. NEVER a peer's response.
   */
  myResponse: AthleticAvailabilityStatusSchema.nullable(),
  /**
   * The CALLER'S OWN note only (self-only). Bounded, untrusted plaintext —
   * reuses the `ATHLETIC_AVAILABILITY_NOTE_MAX` cap; enforced at write time by
   * the (future) write API. Rendered as plain text; escape on any web path.
   */
  myNote: z.string().max(ATHLETIC_AVAILABILITY_NOTE_MAX).nullable(),
  /** What the viewer may do with this item. */
  capabilities: ScheduledItemCapabilitiesSchema
})
export type ScheduledItem = z.infer<typeof ScheduledItemSchema>
